import { useEffect, useMemo, useState } from 'react';
import {
  buildExternalDossierSynthesisPrompt,
  canExportExternalPlanPrompt,
  EXTERNAL_DOSSIER_PROMPT_FILENAME,
} from '../services/externalPlanWorkflow';

const MIN_PLAN_LENGTH = 80;

export default function ExternalPlanInputPanel({
  profileSummary,
  initialText = '',
  disabled = false,
  onSave,
}) {
  const [planText, setPlanText] = useState(initialText);
  const [summaryText, setSummaryText] = useState(profileSummary);
  const [privacyConfirmed, setPrivacyConfirmed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [copyState, setCopyState] = useState('idle');
  const [downloadState, setDownloadState] = useState('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setPlanText(initialText || ''); }, [initialText]);
  useEffect(() => {
    setSummaryText(profileSummary || '');
    setPrivacyConfirmed(false);
    setConfirmed(false);
    setCopyState('idle');
    setDownloadState('idle');
  }, [profileSummary]);

  const promptText = useMemo(
    () => buildExternalDossierSynthesisPrompt(summaryText),
    [summaryText],
  );
  const promptReady = canExportExternalPlanPrompt({
    profileSummary: summaryText,
    privacyReviewConfirmed: privacyConfirmed,
  });

  const copyPrompt = async () => {
    if (!promptReady) {
      setCopyState('review_required');
      return;
    }
    try {
      await navigator.clipboard.writeText(promptText);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      setCopyState('error');
    }
  };

  const downloadPrompt = () => {
    if (!promptReady) {
      setDownloadState('review_required');
      return;
    }
    const blob = new Blob([promptText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = EXTERNAL_DOSSIER_PROMPT_FILENAME;
    link.click();
    URL.revokeObjectURL(url);
    setDownloadState('downloaded');
  };

  const save = async () => {
    const normalized = planText.trim();
    if (normalized.length < MIN_PLAN_LENGTH) {
      setError('Colle la synthèse complète avant de continuer.');
      return;
    }
    if (!confirmed) {
      setError('Confirme que ce texte correspond au bon dossier.');
      return;
    }
    if (!privacyConfirmed) {
      setError('Relis et confirme que la synthèse ne contient aucune identité directe.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(normalized, summaryText.trim());
    } catch (caught) {
      setError(caught?.message || 'La sauvegarde n’a pas pu être confirmée.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="jrn-external-flow">
      <article className="jrn-external-flow__summary">
        <div className="jrn-external-flow__step" aria-hidden="true">1</div>
        <div className="jrn-external-flow__eyebrow">Relire avant l’export</div>
        <div className="jrn-external-flow__head">
          <div>
            <h3>Synthèse du profil sans identité directe</h3>
            <p>Corrige la synthèse si nécessaire, puis confirme qu’elle ne contient plus d’identité directe.</p>
          </div>
          <span className="jrn-external-flow__badge">Sans identité directe</span>
        </div>
        <label className="jrn-external-flow__label" htmlFor="external-profile-summary">
          Synthèse à relire et corriger si nécessaire
        </label>
        <textarea
          id="external-profile-summary"
          className="jrn-external-flow__preview jrn-external-flow__summary-editor"
          value={summaryText}
          rows={18}
          onChange={(event) => {
            setSummaryText(event.target.value);
            setPrivacyConfirmed(false);
            setConfirmed(false);
            setCopyState('idle');
            setDownloadState('idle');
          }}
          disabled={disabled || busy}
        />
        <label className="jrn-external-flow__confirmation jrn-external-flow__privacy-check">
          <input
            type="checkbox"
            checked={privacyConfirmed}
            onChange={(event) => {
              setPrivacyConfirmed(event.target.checked);
              setCopyState('idle');
              setDownloadState('idle');
              setError(null);
            }}
            disabled={disabled || busy || !summaryText.trim()}
          />
          <span>
            J’ai relu et retiré les noms, adresses, lieux précis, téléphones,
            e-mails, identifiants directs et noms de tiers. Le filtre automatique aide,
            mais ne remplace pas cette relecture.
          </span>
        </label>
      </article>

      <article className="jrn-external-flow__prompt">
        <div className="jrn-external-flow__step" aria-hidden="true">2</div>
        <div className="jrn-external-flow__eyebrow">Envoyer le dossier</div>
        <h3>Prompt de synthèse prêt à l’emploi</h3>
        <p>
          Ajoute les PDF originaux dans ta conversation de travail, puis utilise ce dossier unique.
          Il demande de ne recopier aucune identité directe dans la synthèse finale.
        </p>
        {promptReady ? (
          <details className="jrn-external-flow__prompt-preview">
            <summary>Aperçu du prompt de synthèse</summary>
            <pre>{promptText}</pre>
          </details>
        ) : (
          <div className="jrn-external-flow__prompt-locked" aria-hidden="true">
            Le pack apparaîtra après la confirmation de l’étape 1.
          </div>
        )}
        <div className="jrn-external-flow__tools">
          <button type="button" className="jrn-btn jrn-btn--hero" onClick={copyPrompt}
            disabled={disabled || busy || !promptReady}>
            {copyState === 'copied' ? 'Prompt de synthèse copié' : 'Copier le prompt de synthèse'}
          </button>
          <button type="button" className="jrn-btn jrn-btn--soft" onClick={downloadPrompt}
            disabled={disabled || busy || !promptReady}>
            {downloadState === 'downloaded' ? 'Prompt téléchargé' : 'Télécharger le prompt'}
          </button>
        </div>
        {!privacyConfirmed && (
          <p className="jrn-external-flow__locked">La confirmation de l’étape 1 déverrouille le prompt.</p>
        )}
        {copyState === 'error' && (
          <span className="jrn-external-flow__copy-error" role="alert">
            Copie automatique indisponible. Ouvre l’aperçu et sélectionne le texte.
          </span>
        )}
        {(copyState === 'review_required' || downloadState === 'review_required') && (
          <span className="jrn-external-flow__copy-error" role="alert">
            Confirme d’abord la relecture de la synthèse.
          </span>
        )}
      </article>

      <article className="jrn-external-flow__import">
        <div className="jrn-external-flow__step" aria-hidden="true">3</div>
        <div className="jrn-external-flow__eyebrow">Revenir avec le résultat</div>
        <h3>Coller la synthèse d’exploitation</h3>
        <p>
          Colle ici la réponse obtenue. Elle sera enregistrée comme synthèse à valider,
          puis transmise au générateur du SaaS.
        </p>
        <label className="jrn-external-flow__label" htmlFor="external-plan-text">
          Synthèse complète
        </label>
        <textarea
          id="external-plan-text"
          value={planText}
          onChange={(event) => {
            setPlanText(event.target.value);
            setConfirmed(false);
            setError(null);
          }}
          rows={18}
          spellCheck="true"
          placeholder="Colle ici la synthèse d’exploitation complète…"
          className="jrn-external-flow__textarea"
          disabled={disabled || busy}
        />
        <div className="jrn-external-flow__meter" aria-live="polite">
          {planText.trim().length.toLocaleString('fr-CH')} caractères
        </div>
        <label className="jrn-external-flow__confirmation">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => {
              setConfirmed(event.target.checked);
              setError(null);
            }}
            disabled={disabled || busy}
          />
          <span>
            J’ai vérifié que ce texte correspond au bon dossier et ne recopie aucune identité directe.
            La synthèse reste à valider avant la génération du plan.
          </span>
        </label>
        {error && <div className="jrn-error" role="alert">{error}</div>}
        <button
          type="button"
          className="jrn-btn jrn-btn--hero jrn-external-flow__save"
          onClick={save}
          disabled={disabled || busy || !confirmed || !privacyConfirmed
            || planText.trim().length < MIN_PLAN_LENGTH}
          aria-busy={busy}
        >
          {busy ? 'Enregistrement confirmé en cours…' : 'Enregistrer la synthèse et continuer'}
        </button>
        <p className="jrn-external-flow__gate">
          Aucun plan n’est généré ni publié avant la validation explicite d’Anissa.
        </p>
      </article>
    </div>
  );
}
